import type { CoreElement, Entity } from '@tunasong/schemas'

/** These are either set server-side or reserved (such as yDoc) */
export const embedReserved = ['yDoc', 'acls', 'version', 'versionAt', 'lastEditedBy']
export const entityReserved = [
  ...embedReserved,
  'id',
  'userId',
  'updatedAt',
  'createdAt',
  'quota',
  'usage',
  '__deleted__',
  '__expires__',
  '__ruleTrace__',
  /** Media references not lent until the owner confirms them, set at an ownership transfer (GH #2233) */
  'unconfirmedMediaIds',
  /** A share root's async fan-out generation; only the share API writes it (GH #2856) */
  'shareFanoutId',
]
/**
 * Fields present on `SearchSummary` (search index hits) that have no place on a
 * canonical entity embedded in the slate document. Letting them leak into the
 * inserted element shadows live entity state and breaks downstream consumers
 * (e.g. audio play/stop reading `storageUrls` via id).
 */
export const searchSummaryReserved = ['freetext', 'parentChain', 'embedding']
const remove = <T extends Partial<Entity>>(item: T, reservedKeys: string[]) => {
  const reserved = Object.keys(item).filter(k => reservedKeys.includes(k.trim()))
  const updatedItem = { ...item }
  for (const r of reserved) {
    delete (updatedItem as never)[r]
  }
  return updatedItem
}

export const isEntityReserved = (name: string) => entityReserved.includes(name.trim())

/** Remove keys that are reserved */
export const removeEntityReserved = <T extends Partial<Entity>>(item: T) => remove(item, entityReserved)
/** Remove keys that are reserved when embedding in a Slate editor */
export const removeEmbedReserved = <T extends Partial<Entity>>(item: T) =>
  remove(item, [...embedReserved, ...searchSummaryReserved])

export const editorElement = (entity: Entity): CoreElement => ({
  children: [{ text: '' }],
  ...removeEmbedReserved(entity),
})
